import { useState, useEffect, useMemo } from 'react';
import type { ParsedCSV, FieldMapping } from '../../../../types/extraction';
import { autoMapColumns } from './autoMapColumns';
import { Dropdown, type DropdownOption } from '../../../../components/Dropdown';
import { Button } from '../../../../components/Button';
import { PointedArrowIcon } from '../../../../assets/PointedArrowIcon';
import { SentryService } from '../../../../services/sentry';
import { tourAnchor } from '../../../tours/tourAnchors';
import { useTour } from '../../../tours/useTour';
import { ROSTER_IMPORT_FEATURE, ROSTER_IMPORT_TOURS } from './rosterImportTours';
import './FieldMappingStep.css';
import type { ExtractedRow } from "../../../../types/extraction";

interface FieldMappingStepProps {
  extractedData: ParsedCSV;
  onComplete: (mappedData: ParsedCSV) => void;
  onBack: () => void;
  onRetry?: () => void;
}

// Grouped field options for better organization with descriptive labels
const FIELD_GROUPS = [
  {
    label: 'Core',
    options: [
      { label: 'First Name (player\'s first name)', value: 'first_name' },
      { label: 'Last Name (player\'s last name)', value: 'last_name' },
      { label: 'Jersey Number (#)', value: 'number' },
      { label: 'Position (e.g., SS, OF, P)', value: 'position' },
      { label: 'Class or Grad Year (Freshman, R-Fr, 1, or 2027)', value: 'class' },
      { label: 'School/Organization', value: 'school' },
    ],
  },
  {
    label: 'Physical',
    options: [
      { label: 'Height (e.g., 5\'10")', value: 'height' },
      { label: 'Weight (lbs)', value: 'weight' },
      { label: 'Bats (L/R/S)', value: 'bats' },
      { label: 'Throws (L/R)', value: 'throws' },
      { label: 'Birth Date (DOB)', value: 'birth_date' },
    ],
  },
  {
    label: 'Contact',
    options: [
      { label: 'Player Email', value: 'email' },
      { label: 'Player Phone', value: 'phone' },
      { label: 'Twitter/X Handle', value: 'twitter' },
      { label: 'Instagram Handle', value: 'instagram' },
    ],
  },
  {
    label: 'Location',
    options: [
      { label: 'City (hometown)', value: 'city' },
      { label: 'State', value: 'state' },
      { label: 'Street Address', value: 'address' },
      { label: 'Zip Code', value: 'zip' },
    ],
  },
  {
    label: 'Parent/Guardian',
    options: [
      { label: 'Parent/Guardian Name', value: 'parent_name' },
      { label: 'Parent/Guardian Phone', value: 'parent_phone' },
    ],
  },
  {
    label: 'Coach',
    options: [
      { label: 'Coach Name', value: 'coach_name' },
      { label: 'Coach Phone', value: 'coach_phone' },
    ],
  },
  {
    label: 'Advisor',
    options: [
      { label: 'Advisor Name', value: 'advisor_name' },
      { label: 'Advisor Phone', value: 'advisor_phone' },
    ],
  },
  {
    label: 'Team',
    options: [
      { label: 'Team / Color', value: 'team_color' },
      { label: 'Team Number', value: 'team_number' },
    ],
  },
  {
    label: 'Other',
    options: [
      { label: 'Notes / Comments', value: 'notes' },
    ],
  },
];

/**
 * Get sample values from extracted data for a given column
 * Returns up to 3 unique non-empty values
 */
function getSampleValues(rows: ExtractedRow[], columnName: string): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const val = row[columnName];
    if (val != null && val !== '') {
      values.add(String(val).trim());
      if (values.size >= 3) break;
    }
  }
  return Array.from(values);
}

// Flatten groups into options for the dropdown, with group labels as disabled headers
function getFieldOptionsFromGroups(includeShowcaseFields: boolean): DropdownOption[] {
  const options: DropdownOption[] = [{ label: 'Skip Column', value: '' }];

  for (const group of FIELD_GROUPS) {
    // Skip showcase-only fields for single team rosters
    if (group.label === 'Team' && !includeShowcaseFields) {
      continue;
    }

    // Add group header as disabled option
    options.push({ label: `── ${group.label} ──`, value: `__group_${group.label}`, disabled: true });

    // Add group options
    for (const opt of group.options) {
      options.push(opt);
    }
  }

  return options;
}

function applyFieldMapping(data: ParsedCSV, mapping: FieldMapping): ParsedCSV {
  const newHeaders: string[] = [];
  const headerMap = new Map<string, string>();

  // Build new headers and mapping
  data.headers.forEach((oldHeader) => {
    const newHeader = mapping[oldHeader] || oldHeader;
    if (newHeader) {  // Skip empty mappings
      newHeaders.push(newHeader);
      headerMap.set(oldHeader, newHeader);
    }
  });

  // Apply mapping to rows
  const newRows = data.rows.map(row => {
    const newRow: ExtractedRow = {};
    data.headers.forEach((oldHeader) => {
      const newHeader = headerMap.get(oldHeader);
      if (newHeader) {
        newRow[newHeader] = row[oldHeader];
      }
    });
    return newRow;
  });

  return {
    ...data,
    headers: newHeaders,
    rows: newRows,
  };
}

export function FieldMappingStep({ extractedData, onComplete, onBack, onRetry }: FieldMappingStepProps) {
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  // The extracted rows arrive as a prop, so the mapping list and preview are there on first render.
  useTour(ROSTER_IMPORT_FEATURE, {
    ready: true,
    hasData: false,
    pageTourKey: ROSTER_IMPORT_TOURS.fieldMapping,
    dataTourKey: null,
  });

  // Get field options based on roster type (include showcase fields only for showcase rosters)
  const availableOptions = useMemo(() => {
    return getFieldOptionsFromGroups(extractedData.rosterType !== 'single_team');
  }, [extractedData.rosterType]);

  useEffect(() => {
    // Set context and log breadcrumb on mount
    SentryService.setUploadContext({
      trace_id: '', // Will be set by parent
      operation: 'field_mapping',
      phase: 'mapping',
    });

    SentryService.addUploadBreadcrumb('Started field mapping', {
      column_count: extractedData.headers.length,
      row_count: extractedData.rowCount,
      roster_type: extractedData.rosterType,
    });

    // Auto-map columns on mount or when headers change
    const autoMapping = autoMapColumns(extractedData.headers);
    setFieldMapping(autoMapping);
  }, [extractedData.headers]); // Changed from [extractedData]

  const validateMapping = (): boolean => {
    const mappedFields = Object.values(fieldMapping);
    const hasFirstName = mappedFields.includes('first_name');
    const hasLastName = mappedFields.includes('last_name');

    if (!hasFirstName || !hasLastName) {
      const validationErrors = ['First name and last name are required fields'];
      setErrors(validationErrors);

      // Log validation failure to Sentry (warning level - not blocking, user can fix)
      SentryService.captureError('[UPLOAD FAILED] Field mapping validation failed', {
        operation: 'field_mapping',
        phase: 'validation',
        level: 'warning',
        tags: {
          error_category: 'extraction_failed',
          upload_phase: 'field_mapping',
          screen: 'FieldMappingStep',
        },
        extra: {
          has_first_name: hasFirstName,
          has_last_name: hasLastName,
          mapped_fields: mappedFields,
          column_count: extractedData.headers.length,
        },
        fingerprint: ['roster-upload', 'field-mapping', 'validation'],
      });

      return false;
    }

    setErrors([]);
    return true;
  };

  const handleContinue = () => {
    if (!validateMapping()) return;

    // Log successful field mapping
    const mappedFieldCount = Object.values(fieldMapping).filter(v => v).length;
    SentryService.addUploadBreadcrumb('Field mapping completed', {
      mapped_field_count: mappedFieldCount,
      total_columns: extractedData.headers.length,
    });

    // Apply mapping to rows
    const mappedData = applyFieldMapping(extractedData, fieldMapping);
    onComplete(mappedData);
  };

  const handleMappingChange = (header: string, value: string) => {
    setFieldMapping({ ...fieldMapping, [header]: value });
    // Clear errors when user makes changes
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  return (
    <div className="field-mapping-step">
      <div className="field-mapping-header">
        <div className="field-mapping-header-row">
          <h3>Map Fields</h3>
          {onRetry && (
            <button
              className="retry-extraction-button"
              onClick={onRetry}
              type="button"
              {...tourAnchor('roster-import.mapping.reextract')}
            >
              Re-extract
            </button>
          )}
        </div>
        <p>Map the extracted columns to standard prospect fields. First name and last name are required.</p>
      </div>

      <div className="data-preview-section">
        <button
          className="data-preview-toggle"
          onClick={() => setShowPreview(!showPreview)}
          type="button"
          {...tourAnchor('roster-import.mapping.preview')}
        >
          <span className={`preview-chevron ${showPreview ? 'open' : ''}`}>&#9654;</span>
          Preview Data ({extractedData.rowCount} players)
        </button>
        {showPreview && (
          <div className="data-preview-table-wrapper">
            <table className="data-preview-table">
              <thead>
                <tr>
                  {extractedData.headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {extractedData.rows.map((row, idx) => (
                  <tr key={idx}>
                    {extractedData.headers.map((header) => (
                      <td key={header}>{row[header] != null ? String(row[header]) : ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mapping-table" {...tourAnchor('roster-import.mapping.columns')}>
        <div className="mapping-table-header">
          <div className="header-cell">Your Column</div>
          <div className="header-cell"></div>
          <div className="header-cell">Maps To Field</div>
        </div>

        {extractedData.headers.map((header, index) => {
          const sampleValues = getSampleValues(extractedData.rows, header);
          return (
            <div key={index} className="mapping-row">
              <div className="extracted-column">
                <span className="column-name">"{header}"</span>
                {sampleValues.length > 0 && (
                  <span className="sample-values">
                    e.g., {sampleValues.map(v => `"${v}"`).join(', ')}
                  </span>
                )}
              </div>
              <div className="arrow">
                  <PointedArrowIcon className="mapping-arrow-icon" />
              </div>
              <div className="mapping-dropdown">
                <Dropdown
                  label=""
                  value={fieldMapping[header] || ''}
                  onSelect={(value) => handleMappingChange(header, value)}
                  options={availableOptions}
                  placeholder="Skip this column"
                />
              </div>
            </div>
          );
        })}
      </div>

      {errors.length > 0 && (
        <div className="error-message">{errors.join(', ')}</div>
      )}

      <div className="button-group">
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <Button variant="primary" onClick={handleContinue}>Continue</Button>
      </div>
    </div>
  );
}
